import type { Resource } from './resources.ts'

export const ADDITIONAL_EN_RESOURCES: ReadonlyArray<Resource> = [
  {
    language: 'en',
    type: 'guide',
    slug: 'digital-employees',
    title: 'Digital Employees: What They Are and What They Can Do',
    description:
      'Learn how a digital employee differs from a chatbot and traditional automation—and how to embed an AI agent into a real business process.',
    author: 'Laplace Team',
    publishedAt: '2026-08-21',
    readingTime: '14 min read',
    coverImage: '/images/digital-employees-guide.png',
    coverImageAlt:
      'A digital employee connects documents, code, calendars, email, and analytics in one workflow',
    topics: [
      'Digital employees',
      'AI agents for business',
      'Automation',
      'Digital team',
    ],
    integrations: [
      'Jira',
      'Confluence',
      'GitHub',
      'Documents',
      'Email',
      'Calendars',
    ],
    // About the earlier Laplace product (Jira, Confluence, GitHub agents),
    // not what laplace-gtm.com sells; hidden from the GTM site.
    published: false,
    seoTitle: 'Digital Employees: Roles, Tasks, and Adoption | Laplace',
    seoDescription:
      'What a digital employee is, how an AI agent differs from a chatbot, and how to embed a digital team into business processes with human control.',
    relatedResources: ['what-is-context-debt', 'choose-ai-workflow'],
    ctaType: 'find-customers',
    keyTakeaways: [
      'A digital employee is not an autonomous replacement for a person. It is an AI agent with a defined role, instructions, tools, and boundaries.',
      'Agents are useful when work repeatedly requires collecting, checking, and comparing information across several business systems.',
      'Adoption should begin with one limited process, a verifiable outcome, and predefined points of human control.',
    ],
    sections: [
      {
        id: 'why-separate-ai-chats-are-not-enough',
        title: 'Why separate AI chats are not enough for a company',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Work context rarely lives in one place. Tasks are tracked in Jira, documentation in Confluence, code and pull requests in GitHub, and meetings in calendars. Supporting materials live in files, while some important decisions remain in messages or in the memory of individual employees.',
          },
          {
            type: 'paragraph',
            text: 'To understand the state of a project, a person has to move between systems, find related documents, check task statuses, and compare code changes with the original requirements. Leaders regularly request summaries, and teams rebuild context before planning, releases, and handoffs.',
          },
          {
            type: 'paragraph',
            text: 'Access to a language model does not solve this problem by itself. A model can analyze the text it receives, but it does not automatically know what is happening in the company’s projects right now. If an employee manually copies tasks, documents, and comments into a chat, AI remains a separate tool rather than part of the workflow.',
          },
          {
            type: 'paragraph',
            text: 'Digital employees offer a different approach. An AI agent receives a defined role, works with permitted company sources, and prepares a concrete result such as an operational summary, feature context, or a draft of release notes.',
          },
        ],
      },
      {
        id: 'what-is-a-digital-employee',
        title: 'What is a digital employee?',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A digital employee is a configured AI agent for business that is embedded into a specific company process. It is not an autonomous replacement for a person or a virtual copy of a profession.',
          },
          {
            type: 'paragraph',
            text: 'It is more useful to think of a digital employee as a software participant in a process with the following elements:',
          },
          {
            type: 'list',
            items: [
              'Role. The agent needs to know which part of the work it owns: collecting project context, reviewing task quality, or preparing a management summary.',
              'Expected result. Define a verifiable output instead of a vague goal such as “help the team”: a blocker report, a list of documentation gaps, or a draft update.',
              'Instructions. They describe the workflow, quality criteria, response format, and situations in which the agent must involve a person.',
              'Working context. The agent uses only the tasks, documents, repositories, and other sources available to it.',
              'Tools. The toolset determines whether the agent can search tasks, read pages, analyze changes, or perform permitted actions.',
              'Permissions and limits. Access should match the workspace, connected systems, and the user’s authority. Permission to read data does not imply permission to change it.',
              'Schedule. A recurring process can run automatically, for example every morning or before a weekly meeting.',
              'Human control. A person reviews conclusions, approves consequential actions, and remains accountable for decisions.',
              'Run history. Results of scheduled runs, errors, and actions that change data should remain available for review.',
            ],
          },
          {
            type: 'callout',
            title: 'A bounded process role, not a simulated profession',
            body: 'An AI employee is useful not because it imitates a person, but because it consistently performs a defined part of the work within explicit boundaries.',
          },
        ],
      },
      {
        id: 'agent-vs-chatbot-and-automation',
        title:
          'How an AI agent differs from a chatbot and traditional automation',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'These approaches can work together, but they solve different kinds of problems:',
          },
          {
            type: 'list',
            items: [
              'A chatbot answers an individual question within a conversation and the context provided to it. It works well for explanations, consultations, and one-off requests.',
              'Traditional automation follows a predefined sequence of actions and conditions. It is effective in stable processes with unambiguous rules.',
              'An AI agent analyzes available context, selects appropriate tools, and prepares a result within its role. It is useful when work involves searching, comparing, and interpreting data from several sources.',
            ],
          },
          {
            type: 'paragraph',
            text: 'A chatbot starts after a user sends a message. It usually does not monitor the state of a workflow or know which sources to check unless their contents were supplied in the conversation.',
          },
          {
            type: 'paragraph',
            text: 'Traditional automation handles rules such as “when X happens, move data from system A to system B.” It has more difficulty with ambiguous context—for example, determining why a task is blocked or which changes are missing from the documentation.',
          },
          {
            type: 'paragraph',
            text: 'An AI agent can select tools for a request, collect data from permitted systems, compare it, and prepare a conclusion. That does not make the agent fully autonomous: its capabilities remain limited by its tools, permissions, instructions, and points of human control.',
          },
        ],
      },
      {
        id: 'why-model-access-is-not-enough',
        title: 'Why access to a model is not enough',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A general language model does not know the current state of tasks, internal decisions, project structure, or ownership in a particular company. It has no automatic access to corporate documentation and no permission to act on a user’s behalf.',
          },
          {
            type: 'paragraph',
            text: 'Even if a large amount of data is uploaded once, the working context changes quickly. Jira gains new statuses and comments, GitHub gains code changes, Confluence gains updated decisions, and calendars gain new meetings.',
          },
          {
            type: 'paragraph',
            text: 'Embedding artificial intelligence into business processes therefore requires more than a model. It requires a managed connection to work systems. An agent should receive information through connected tools, respect the current workspace, and never expand the user’s permissions.',
          },
          {
            type: 'paragraph',
            text: 'It is equally important to show sources, separate retrieved facts from assumptions, and disclose when data is missing. If an agent cannot find a decision in the documentation, it should identify the gap rather than confidently invent an answer.',
          },
        ],
      },
      {
        id: 'how-laplace-implements-digital-employees',
        title: 'How digital employees work in Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In Laplace, organization members can create configurable agents. Each agent can have a name, role description, instructions, icon, tags, and an available toolset. The expected result can be defined in the description and instructions, and recurring work can use a dedicated automation prompt.',
          },
          {
            type: 'paragraph',
            text: 'An agent is available across the organization’s workspaces, while its tools run in the context of the current workspace. This makes it possible to create specialized roles without giving every agent access to every platform capability.',
          },
          {
            type: 'list',
            items: [
              'Jira: read tasks, comments, participants, and sprints and perform permitted actions.',
              'Confluence: search, read, create, and update pages.',
              'GitHub: work with repositories, issues, pull requests, comments, files, project trees, and diffs.',
              'Workspace documents: PDF, DOCX, PPTX, XLSX, Markdown, and TXT with semantic search.',
              'Email: send through Gmail; search, read, send, reply, and create drafts through Yandex Mail.',
              'Google, Yandex, and Outlook calendars: read events without changing them.',
            ],
          },
          {
            type: 'paragraph',
            text: 'A recurring automation is configured with a workspace, agent, prompt, schedule, and time zone. The result of a background run is saved as a regular chat with automation metadata. Run history, statuses, and retry after an error are available.',
          },
          {
            type: 'callout',
            title: 'Current automation boundaries',
            body: 'This is not a universal builder for arbitrary multistep workflows. The current Laplace model focuses on running one configured agent on a schedule through the tools available to that agent.',
          },
          {
            type: 'paragraph',
            text: 'Context Audit is a separate platform capability. It helps identify problems in working context: incomplete tasks, documentation gaps, weak links between tasks and changes, unresolved decisions, and other forms of Context Debt. Proposed Jira changes remain drafts until a person explicitly approves them.',
          },
        ],
      },
      {
        id: 'digital-employee-examples',
        title: 'Examples of digital employees you can configure',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'The following roles are not built-in templates. They are examples of AI agents for business that can be configured in Laplace around a company’s own processes.',
          },
        ],
      },
      {
        id: 'digital-project-coordinator',
        title: 'Digital project coordinator',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'A project lead often has to reconstruct project status before meetings and repeatedly ask the team for updates.',
          },
          {
            type: 'list',
            items: [
              'Sources: Jira, Confluence, GitHub, and the active calendar.',
              'Agent work: review completed and overdue tasks, related code changes, documented decisions, and upcoming meetings; highlight blockers, risks, and unanswered questions.',
              'Human result: a daily or weekly summary of key changes and decisions that require attention.',
              'Human control: the lead reviews the conclusions and decides on deadlines, priorities, and ownership.',
            ],
          },
        ],
      },
      {
        id: 'task-quality-assistant',
        title: 'Task quality assistant',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Tasks can enter a workflow without a clear description, acceptance criteria, priority, estimate, or owner.',
          },
          {
            type: 'list',
            items: [
              'Sources: Jira tasks and comments, plus related Confluence pages.',
              'Agent work: find incomplete tasks, explain which information is missing, and draft improvements to the description or acceptance criteria.',
              'Human result: a list of problematic tasks and draft additions for process owners.',
              'Human control: the agent does not change Jira on its own; a person reviews the proposals and explicitly approves any required changes.',
            ],
          },
        ],
      },
      {
        id: 'digital-technical-editor',
        title: 'Digital technical editor',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Documentation gradually diverges from implemented product behavior when updates are not captured alongside development.',
          },
          {
            type: 'list',
            items: [
              'Sources: Jira, Confluence, GitHub, and workspace documents.',
              'Agent work: compare requirements, existing pages, and code changes; find outdated descriptions, missing sections, and undocumented decisions.',
              'Human result: a list of discrepancies and draft documentation updates.',
              'Human control: a technical specialist validates the text before publication or a page update.',
            ],
          },
        ],
      },
      {
        id: 'development-assistant',
        title: 'Development assistant',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Before starting work on a feature or bug, a developer spends time reconstructing the related context.',
          },
          {
            type: 'list',
            items: [
              'Sources: Jira, Confluence, GitHub, and uploaded workspace documents.',
              'Agent work: collect requirements, decision history, related tasks, pull requests, issues, and relevant files; identify contradictions and missing information.',
              'Human result: one source-linked overview of a feature, bug, or technical decision with open questions.',
              'Human control: the developer reviews the findings and independently chooses the implementation approach.',
            ],
          },
        ],
      },
      {
        id: 'release-preparation-assistant',
        title: 'Release preparation assistant',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Before a release, information about tasks, changes, and open questions often has to be gathered manually from several systems.',
          },
          {
            type: 'list',
            items: [
              'Sources: Jira, Confluence, and GitHub.',
              'Agent work: collect related tasks and changes, check links between pull requests and tasks, surface unresolved questions, and prepare draft release notes.',
              'Human result: a structured information package for release review.',
              'Human control: the agent does not decide whether a release is ready; product, engineering, and quality owners approve the release.',
            ],
          },
        ],
      },
      {
        id: 'team-lead-assistant',
        title: 'Team lead assistant',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'A significant share of a team lead’s time can be spent repeatedly collecting operational information.',
          },
          {
            type: 'list',
            items: [
              'Sources: Jira, Confluence, GitHub, and the calendar.',
              'Agent work: on a schedule, analyze task movement, blockers, documentation gaps, unresolved decisions, and events that require preparation.',
              'Human result: an operational summary with priority questions and links to source data.',
              'Human control: the lead decides which issues require intervention and remains accountable for management decisions.',
            ],
          },
        ],
      },
      {
        id: 'which-processes-fit',
        title: 'Which processes are a good fit for digital employees?',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A good first scenario does not need to be the largest one. It matters more that the scope can be limited, tested, and compared with the current way of working.',
          },
          {
            type: 'list',
            items: [
              'The task repeats regularly.',
              'The result requires data from several systems.',
              'The work follows understandable rules.',
              'The output is concrete and verifiable.',
              'The process consumes meaningful specialist time.',
              'A person can review the result.',
              'The scenario does not require unlimited access or independent critical decisions.',
            ],
          },
          {
            type: 'paragraph',
            text: 'It is often safer to begin with information preparation, recommendations, and drafts. Once quality is proven, the team can gradually expand the tools and routine automation available to the agent.',
          },
        ],
      },
      {
        id: 'how-to-start',
        title: 'How to introduce a digital employee',
        level: 2,
        blocks: [
          {
            type: 'list',
            items: [
              '1. Choose one recurring process. Do not begin with “automate the department.” Select a concrete task such as a weekly project summary.',
              '2. Record the current state. Describe participants, manual steps, common problems, and an acceptable outcome to create a baseline.',
              '3. Identify data sources. Connect only the systems and documents required to produce the result.',
              '4. Define the agent’s role and output. Specify the response format, quality criteria, and accountability boundaries.',
              '5. Limit available actions. If reading and drafting are enough, the agent does not need permission to change external systems.',
              '6. Set points of human control. Decide which outputs need review, which require explicit approval, and which actions the agent must never perform.',
              '7. Run a limited pilot. Use one workspace, one team, or one project and collect examples of errors and exceptions.',
              '8. Evaluate time and quality. Compare manual steps, output completeness, accepted recommendations, and recurring errors with the baseline.',
              '9. Expand only after validation. Add sources, schedules, actions, or adjacent processes after the pilot is stable.',
            ],
          },
          {
            type: 'callout',
            title: 'Scale processes that have proved useful',
            body: 'A digital team should grow around validated scenarios, not around the number of agents created.',
          },
        ],
      },
      {
        id: 'digital-team-not-chatbots',
        title:
          'A digital team is a system of roles, not a collection of chatbots',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A digital team does not emerge when a company creates many separate AI chats. It consists of specialized agents, each connected to a concrete business process, supplied with the necessary context, and constrained by clear boundaries.',
          },
          {
            type: 'paragraph',
            text: 'People remain accountable for decisions, priorities, and actions with significant consequences. Agents can take on recurring search, collection, checking, comparison, and preparation of information.',
          },
          {
            type: 'paragraph',
            text: 'This approach moves a company from occasional model use toward systematic adoption of artificial intelligence in business processes.',
          },
          {
            type: 'callout',
            title: 'A digital team built around real work',
            body: 'Laplace helps build a digital team around real company processes—with working context, integrations, automations, and human control.',
          },
        ],
      },
      {
        id: 'faq',
        title: 'Frequently asked questions',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'Can a digital employee completely replace a person?',
                answer:
                  'No. A digital employee is designed for a limited role within a process. It can collect data, compare sources, prepare recommendations, and perform permitted actions, but a person remains accountable for consequential decisions.',
              },
              {
                question:
                  'How is a digital employee different from a regular chatbot?',
                answer:
                  'A chatbot usually answers an individual question in a conversation. A digital employee has a defined role, instructions, tools, working context, and, when needed, a schedule. Its output is tied to a specific company process.',
              },
              {
                question: 'Which tasks should be automated first?',
                answer:
                  'Start with recurring tasks that require information from several systems and produce a verifiable result: project summaries, task quality reviews, context preparation, or release notes.',
              },
              {
                question: 'Can the actions of an AI employee be controlled?',
                answer:
                  'Yes. Control comes from the available toolset, user and workspace permissions, agent instructions, and points of human approval. In sensitive scenarios, the agent can be limited to reading and drafting.',
              },
              {
                question:
                  'Does a company need a dedicated AI team to get started?',
                answer:
                  'For a limited pilot, involvement from the process owner and specialists who understand the data sources and quality criteria matters most. A technical or AI team becomes especially useful when scaling and connecting sensitive systems.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'en',
    type: 'guide',
    slug: 'choose-ai-workflow',
    title:
      'Why Companies Automate the Wrong Processes—and How to Find the Right AI Workflow',
    description:
      'Learn how to reconstruct a real workflow, find its bottlenecks, and choose a process where AI can create verifiable value.',
    author: 'Laplace Team',
    publishedAt: '2026-08-21',
    readingTime: '13 min read',
    coverImage: '/images/choose-ai-workflow-guide.png',
    coverImageAlt:
      'Process Discovery compares digital traces with a process participant’s explanation',
    topics: [
      'Process Discovery',
      'AI automation',
      'Business processes',
      'Digital team',
    ],
    integrations: ['Jira', 'Confluence', 'GitHub', 'Calendars'],
    published: false,
    seoTitle: 'How to Choose a Business Process for AI Automation',
    seoDescription:
      'Why AI projects automate the wrong tasks, how to reconstruct a real workflow, and how to choose a process for a controlled AI pilot.',
    relatedResources: ['what-is-context-debt', 'digital-employees'],
    ctaType: 'context-audit',
    keyTakeaways: [
      'The first question in an AI project is not which agent to build, but how the work is actually performed and why it slows down.',
      'A real workflow can be reconstructed only by comparing digital traces, formal procedures, and explanations from process participants.',
      'Laplace is developing Process Discovery Agent as a future step between Context Audit, digital team design, and a measurable pilot.',
    ],
    sections: [
      {
        id: 'wrong-first-question',
        title: 'Companies start automation with the wrong question',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Discussions about AI adoption often begin with technology. A team decides which model to use, which agent to build, which systems to connect, and which tasks to assign to it.',
          },
          {
            type: 'paragraph',
            text: 'Executives want to know where savings can appear fastest. Technical teams compare platforms and integration approaches. Process participants suggest automating the activities that feel most time-consuming. These questions matter, but they arrive too early.',
          },
          {
            type: 'paragraph',
            text: 'Before choosing an AI agent, a company needs to answer more basic questions:',
          },
          {
            type: 'list',
            items: [
              'How is the process performed today, rather than how the procedure says it should work?',
              'Which people, systems, and documents take part?',
              'Where do delays and manual handoffs occur?',
              'What information is required for each decision?',
              'Which exceptions are absent from the instructions?',
              'Where are the accountability boundaries between participants?',
              'What does a concrete and verifiable process outcome look like?',
            ],
          },
          {
            type: 'paragraph',
            text: 'Without this picture, a company risks automating the most visible task rather than the actual bottleneck. A product team may try to accelerate coding even though most delays happen earlier, during task definition and requirements alignment.',
          },
          {
            type: 'callout',
            title: 'Start with the process, not the agent',
            body: 'The first question in an AI project should not be “which agent should we build?” It should be “how is this work actually performed, and why does it slow down?”',
          },
        ],
      },
      {
        id: 'formal-vs-real-process',
        title: 'The documented process rarely matches the real one',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In a procedure, a workflow appears sequential: one participant completes a stage, hands the result to the next, and every decision is recorded in the designated system. Real work is more complicated.',
          },
          {
            type: 'paragraph',
            text: 'Tasks and statuses live in Jira. Requirements and architecture decisions are stored in Confluence. Implemented changes appear in GitHub. Some agreements are made in meetings. Supporting materials remain in documents, while certain exceptions exist only in the memory of experienced employees.',
          },
          {
            type: 'paragraph',
            text: 'A formal process shows how work is supposed to happen. Digital traces show individual facts: who changed a task, when a pull request appeared, or which page was updated. Neither source explains the complete workflow on its own.',
          },
          {
            type: 'paragraph',
            text: 'An interview alone is not enough. A participant can forget a rare exception, omit a habitual manual step, or describe the process as it should work. Familiar actions can become so automatic that people stop seeing them as separate stages.',
          },
          {
            type: 'paragraph',
            text: 'System data alone is not enough either. It reveals events but not always their causes. Task history may show several status changes without explaining why the task returned to an earlier stage. A calendar confirms that a meeting occurred, but not which decision was made or where it was documented.',
          },
          {
            type: 'callout',
            title: 'No single source is sufficient',
            body: 'A real process cannot be reconstructed from one procedure, one interview, or one system. Digital traces must be compared with a human explanation of the work.',
          },
        ],
      },
      {
        id: 'automation-without-discovery',
        title: 'What happens when a poorly understood process is automated',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'When a company moves to implementation too early, the problems surface during the pilot:',
          },
          {
            type: 'list',
            items: [
              'A visible individual task is automated instead of the true process bottleneck.',
              'The agent receives a task but cannot see related decisions, constraints, and change history.',
              'Rare but important exceptions appear only after the pilot produces errors.',
              'Automation accelerates a stage that is unnecessary or should not exist in its current form.',
              'The team has not defined what the agent may do independently, what requires approval, or who owns the result.',
              'Without baseline metrics, the result cannot be compared with the previous way of working.',
            ],
          },
          {
            type: 'paragraph',
            text: 'The team then keeps expanding instructions, restricting agent actions, and manually compensating for missing context. The pilot may work technically while the total time required to complete the process barely changes.',
          },
          {
            type: 'callout',
            title: 'Automation amplifies the design of a process',
            body: 'If a poorly understood process is automated, the company will not get a new way of working. It will reproduce the old chaos faster.',
          },
        ],
      },
      {
        id: 'how-process-discovery-should-work',
        title: 'How Process Discovery should work',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Process Discovery means reconstructing the actual workflow from work-system data and explanations from its participants. Its purpose is not merely to draw a diagram. It should confirm where work happens, which decisions are made, and what prevents the process from moving forward.',
          },
          {
            type: 'paragraph',
            text: 'In Laplace, Context Audit can serve as the starting point for this analysis. It already helps find working-context problems in available Jira, Confluence, GitHub, and active calendar data: incomplete tasks, documentation gaps, weak links between objects, and unresolved decisions.',
          },
          {
            type: 'paragraph',
            text: 'Context Audit is not a ready-made map of a business process and does not replace interviews with participants. A subsequent stage needs to connect the digital traces it finds with human understanding of the work.',
          },
          {
            type: 'list',
            items: [
              '1. Review Context Audit results to understand which sources are available and where context gaps were found.',
              '2. Collect related tasks, documents, code changes, and calendar events.',
              '3. Separate confirmed facts from hypotheses and unknown areas.',
              '4. Conduct an adaptive interview with a process participant.',
              '5. Compare the participant’s answers with digital traces.',
              '6. Find delays, manual handoffs, and points where context is lost.',
              '7. Build a map of the real workflow, including participants, sources, decisions, and exceptions.',
              '8. Design a digital team for confirmed tasks within the process.',
              '9. Prepare a limited pilot with a baseline, quality criteria, and points of human control.',
            ],
          },
        ],
      },
      {
        id: 'adaptive-interview',
        title: 'Why the interview should be adaptive',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'A process participant does not need a long generic questionnaire. Questions should depend on the data, gaps, and contradictions already discovered.',
          },
          {
            type: 'paragraph',
            text: 'If a task changed status several times, the interview should explore why it returned. If a meeting occurred but no decision appeared in the documentation, the agent should ask where it was recorded. If a pull request is not connected to a task, the important question is whether that is an error or an accepted team practice.',
          },
          {
            type: 'paragraph',
            text: 'A participant’s account should not automatically become the complete process model. It needs to be compared with available tasks, documents, changes, and events. A discrepancy does not always mean the person is wrong: some work may happen outside the connected systems. That is a meaningful finding as well.',
          },
        ],
      },
      {
        id: 'what-laplace-is-developing',
        title: 'What Laplace is developing',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace is working on Process Discovery Agent, a new stage between Context Audit and the creation of a digital team.',
          },
          {
            type: 'paragraph',
            text: 'We are developing an agent that will help connect work-system data with an adaptive interview of a process participant. Instead of asking everyone the same questions, it will ask follow-ups based on discovered gaps, unavailable sources, and contradictions.',
          },
          {
            type: 'paragraph',
            text: 'We plan for Process Discovery Agent to help prepare the following outputs:',
          },
          {
            type: 'list',
            items: [
              'A data-supported process model.',
              'A visual workflow map.',
              'A list of delays and manual handoffs.',
              'A list of unknown or inaccessible areas.',
              'AI opportunities within the process.',
              'A digital team design.',
              'A pilot plan with a baseline and success criteria.',
              'An assessment of confidence in the findings.',
            ],
          },
          {
            type: 'callout',
            title: 'Process Discovery Agent is in development',
            body: 'This is not an already available capability of Context Audit or current Laplace agents. We are planning it as the next stage from process investigation to a controlled pilot.',
          },
        ],
      },
      {
        id: 'feature-development-example',
        title: 'Example: developing a new product feature',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Consider a product team that wants to release features faster. In the first discussion, participants identify coding speed as the main problem. A development assistant seems like the obvious solution.',
          },
          {
            type: 'paragraph',
            text: 'Analysis of the actual workflow reveals a different picture:',
          },
          {
            type: 'list',
            items: [
              'Tasks reach development without acceptance criteria.',
              'Decisions made in meetings do not always reach Confluence.',
              'Developers repeatedly clarify requirements with the product manager.',
              'Pull requests are not always linked to their source tasks.',
              'Documentation is updated after release or not updated at all.',
            ],
          },
          {
            type: 'paragraph',
            text: 'In the proposed scenario, Process Discovery Agent will compare Jira tasks, Confluence pages, GitHub changes, and calendar events. It will then ask about the reasons tasks returned, where decisions were recorded, how code should link to tasks, and who owns documentation updates.',
          },
          {
            type: 'paragraph',
            text: 'After comparing the data and answers, the team may discover that the main delay is not coding. Time is lost while context moves between task definition, implementation, and release preparation.',
          },
        ],
      },
      {
        id: 'digital-team-project',
        title: 'What a digital team design could include',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'For this process, a digital team design could include:',
          },
          {
            type: 'list',
            items: [
              'An agent that reviews task definition quality.',
              'An agent that reconstructs context for a feature.',
              'A digital technical editor.',
              'A release preparation assistant.',
            ],
          },
          {
            type: 'paragraph',
            text: 'A coding assistant may still be useful, but it is no longer treated as the only or the first solution.',
          },
          {
            type: 'paragraph',
            text: 'The pilot runs with one team and one workflow type. Before it begins, the team records task cycle time, repeated clarification, returns, and missing links between tasks, documentation, and changes. The company then measures the effect of the digital team on a concrete process rather than the general ability of AI to generate text.',
          },
        ],
      },
      {
        id: 'unknowns-and-confidence',
        title: 'Why Process Discovery should show unknowns and confidence',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A language model can produce a plausible explanation from incomplete data. For business-process investigation, that capability can be dangerous.',
          },
          {
            type: 'paragraph',
            text: 'If part of the workflow is not visible in connected systems, an agent should not automatically invent the missing stages. Every significant conclusion should show:',
          },
          {
            type: 'list',
            items: [
              'Which data supports the conclusion.',
              'What the process participant reported.',
              'Which sources were unavailable.',
              'Where data contradicts the explanation.',
              'How reliable the conclusion is.',
              'Which information needs manual verification.',
            ],
          },
          {
            type: 'paragraph',
            text: 'For example, an agent may confirm that a task returned to active work three times without finding the reason. An interview can add the participant’s explanation, but it remains unverified if the decision was not recorded anywhere. The process problem may be precisely that decision reasons are never preserved.',
          },
          {
            type: 'paragraph',
            text: 'Confidence should not become one opaque universal score. A team benefits more from understanding the evidence: which facts are confirmed, which conclusions rely on interviews, and where unknown areas remain.',
          },
          {
            type: 'callout',
            title: 'The limits of knowledge are part of the result',
            body: 'Good Process Discovery does not only describe a process. It honestly shows the limits of what it knows.',
          },
        ],
      },
      {
        id: 'how-to-choose-first-workflow',
        title: 'How to choose the first workflow for AI',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A suitable first process should be important enough for the result to matter and limited enough for the team to run a controlled pilot.',
          },
          {
            type: 'list',
            items: [
              'The process repeats regularly.',
              'It has a concrete start and finish.',
              'It crosses several systems or participants.',
              'It contains visible manual handoffs.',
              'Its outcome can be verified.',
              'Consequential actions allow human approval.',
              'The current state can be recorded as a baseline.',
              'The process has an owner who wants to improve it.',
            ],
          },
          {
            type: 'paragraph',
            text: 'The first pilot does not need to target the most expensive process. A workflow that makes it possible to test a hypothesis quickly, discover exceptions, and compare results with a baseline is often a better starting point.',
          },
        ],
      },
      {
        id: 'understand-before-automating',
        title: 'Understand the work before building a digital team',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'The first step toward a digital team is not creating an agent. A company first needs to understand where the real work happens, why it slows down, and which part should actually be assigned to AI.',
          },
          {
            type: 'paragraph',
            text: 'A procedure provides only the intended design. Work-system data shows individual facts. Interviews explain causes and exceptions. Only their combined analysis can reconstruct the process and identify a suitable automation scenario.',
          },
          {
            type: 'callout',
            title: 'From audit to a measurable pilot',
            body: 'Laplace is developing a path from Context Audit through Process Discovery and digital team design to a measurable pilot. If you want to test this approach on one of your team’s processes, book a demo.',
          },
        ],
      },
      {
        id: 'faq-process-discovery',
        title: 'Frequently asked questions',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'What is Process Discovery?',
                answer:
                  'Process Discovery is the reconstruction of a real business process from work-system data and explanations from its participants. The result should be an evidence-supported map of stages, decisions, participants, delays, and unknown areas.',
              },
              {
                question:
                  'How is Process Discovery different from Context Audit?',
                answer:
                  'Context Audit helps find working-context problems in available sources. Process Discovery should go further by comparing digital traces with an adaptive interview and building a model of a specific workflow. Process Discovery Agent in Laplace is still in development.',
              },
              {
                question: 'Why are employee interviews not enough?',
                answer:
                  'A participant may forget a rare step, omit a familiar manual action, or describe the process according to the formal procedure. Their answers need to be compared with tasks, documents, changes, and other digital traces.',
              },
              {
                question:
                  'How do you know whether a process is suitable for AI automation?',
                answer:
                  'The process should repeat regularly, have a verifiable result and a clear owner, and allow human control. It is especially suitable when it includes meaningful search, data comparison, or manual transfer of context.',
              },
              {
                question: 'Do we need to document every company process first?',
                answer:
                  'No. Start with one workflow, one team, or one task type. A limited pilot can test the hypothesis and collect evidence without an expensive investigation of the entire organization.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'en',
    type: 'research',
    slug: 'uber-ai-agents-software-factory',
    title: 'Uber Shows What a Company with Digital Employees Will Look Like',
    description:
      'A closer look at Uber’s Software Factory and why enterprise AI is moving from isolated chats to a managed digital team.',
    author: 'Laplace Team',
    publishedAt: '2026-09-07',
    readingTime: '10 min read',
    coverImage: '/images/uber-ai-agents-software-factory.png',
    coverImageAlt:
      'A managed digital team of specialized AI agents works through a shared process with human oversight',
    topics: [
      'AI agents for business',
      'Digital employees',
      'Business process automation',
      'Enterprise AI',
      'Uber Software Factory',
      'Knowledge Graph',
    ],
    integrations: ['Jira', 'Confluence', 'GitHub'],
    // About the earlier Laplace product (Jira, Confluence, GitHub agents),
    // not what laplace-gtm.com sells; hidden from the GTM site.
    published: false,
    seoTitle: 'AI Agents for Business: Uber’s 9.4× Request Growth',
    seoDescription:
      'How Uber builds a Software Factory with managed AI agents—and why business AI needs specialization, company context, and measurable outcomes.',
    relatedResources: [
      'digital-employees',
      'what-is-context-debt',
      'choose-ai-workflow',
      'ai-native-sdlc-playbook',
      'programmers-manage-ai-coding-agents',
    ],
    ctaType: 'find-customers',
    keyTakeaways: [
      'Uber’s experience shows a shift from isolated AI tools to managed agents that perform specific engineering workflows with human review and escalation.',
      'Agent value should be measured by process outcomes and quality, not by request volume, token usage, or the number of agents created.',
      'Laplace applies the same principle to business workflows: a digital employee needs a role, working context, tools, boundaries, and a verifiable outcome.',
    ],
    sections: [
      {
        id: 'what-uber-published',
        title: 'What Uber published',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In August 2026, Uber explained how it manages AI use across software development at scale. More than 70% of pull requests are attributed to local or cloud agents, employees have built more than 3,600 agent skills, and those skills run more than 30,000 times per day.',
          },
          {
            type: 'paragraph',
            text: 'The architectural conclusion matters more than the headline numbers. Uber is moving from interactive sessions initiated by engineers toward managed agents. These agents review code, help repair CI failures, validate changes visually, triage alerts, and maintain code, while people retain control through reviews and escalations.',
          },
          {
            type: 'metrics',
            items: [
              {
                value: '7×',
                label: 'growth in active users',
                detail: 'from February to August 2026',
              },
              {
                value: '9.4×',
                label: 'growth in agentic requests',
                detail: 'from February to August 2026',
              },
              {
                value: '−34%',
                label: 'cost per 1,000 requests',
                detail: 'from peak with the model held constant',
              },
              {
                value: '−52%',
                label: 'cost per session',
                detail: 'from the June peak with the model held constant',
              },
            ],
            sourceNote:
              'Source: Uber Engineering. Individual results depend on the environment and use case.',
          },
          {
            type: 'source',
            label: 'Primary source',
            description:
              'Uber Engineering, “Running a Software Factory Efficiently at Uber Scale,” August 27, 2026.',
            href: 'https://www.uber.com/us/en/blog/efficient-software-factory/',
          },
          {
            type: 'callout',
            title: 'What we mean by validating a Laplace principle',
            body: 'Uber does not mention or endorse Laplace, and the companies are not presented as partners. The validation is independent: enterprise AI becomes more useful when it moves from a standalone chat into a managed role within a real process.',
          },
        ],
      },
      {
        id: 'from-chat-to-managed-agent',
        title: 'From an AI chat to a managed digital employee',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A typical AI chat responds to an individual request. The user gathers the material, frames the task, transfers the answer into a work system, and repeats the sequence next time. The tool can make a person faster without changing the complete workflow.',
          },
          {
            type: 'paragraph',
            text: 'A managed agent works differently. It has a bounded role, a clear expected outcome, approved tools, working context, and rules for involving a person. It can run on an event or schedule and perform the same process consistently.',
          },
          {
            type: 'list',
            items: [
              'The role defines which part of the process the agent owns.',
              'Tools determine which sources the agent can read and which actions it can perform.',
              'Context connects the request with current tasks, documents, changes, and decisions.',
              'Boundaries define permissions and situations that require human approval.',
              'An outcome metric shows whether the agent improves the process.',
            ],
          },
          {
            type: 'paragraph',
            text: 'At Laplace, we call this process participant a digital employee. It is not a simulated profession or an attempt to remove people from work. It is a specialized AI agent assigned a limited, recurring part of a workflow: compiling project status, checking task quality, preparing context for a customer request, or finding discrepancies between requirements and implementation.',
          },
        ],
      },
      {
        id: 'measure-value-not-activity',
        title: 'Measure AI value, not AI activity',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Uber decomposes AI session spend across users, sessions, turns, requests, tokens, and token price. For managed agents, however, it uses a more meaningful layer of metrics: the cost of a completed outcome together with a quality signal.',
          },
          {
            type: 'list',
            items: [
              'For code review: cost per review, precision, recall, and noise.',
              'For incident work: cost per triaged alert and time to recovery.',
              'For code changes: cost per merged pull request and revert rate.',
            ],
          },
          {
            type: 'paragraph',
            text: 'The same principle applies to business workflows. For a digital project coordinator, the time required to produce an accurate summary and the number of blockers found matter more than message count. For a support agent, useful metrics include time to a ready answer, source coverage, and the amount of specialist editing. For a documentation assistant, they include confirmed discrepancies and accepted updates.',
          },
          {
            type: 'callout',
            title: 'Agent count is not a business metric',
            body: 'A digital team creates value only when each agent improves a concrete outcome by saving time, increasing completeness, reducing errors, or removing repetitive work from specialists.',
          },
        ],
      },
      {
        id: 'context-determines-quality',
        title: 'Agent quality depends on context quality',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In a large organization, an agent may spend more time finding the right information than producing the result. A task can live in Jira, a decision in Confluence, its implementation in GitHub, and a critical exception in discussion history. If the relationships are unclear, AI searches more sources, consumes more tokens, and can still reach the wrong conclusion.',
          },
          {
            type: 'paragraph',
            text: 'Uber illustrates this with one comparison. An agent with access to its internal AI Context Graph located the relevant dataset and answered correctly in 38 seconds. Without the graph, an agent spent more than 20 minutes, encountered errors, and reached the wrong conclusion. This is one Uber experiment rather than a universal speedup estimate, but it shows the value of prepared company context.',
          },
          {
            type: 'paragraph',
            text: 'In the current Laplace product, digital employees receive context through connected work systems and documents within the permissions of a specific workspace. A unified Knowledge Graph that links tasks, documents, changes, decisions, and participants is on the product backlog. We see it as a potential future layer for a more coordinated digital team, but it is not currently an available Laplace feature.',
          },
          {
            type: 'paragraph',
            text: 'Companies can improve the foundation before that architecture exists. If tasks are disconnected from requirements, decisions are undocumented, or documents are stale, an agent inherits those gaps. Laplace Context Audit helps identify this Context Debt and find a process that is ready for AI adoption.',
          },
        ],
      },
      {
        id: 'specialized-models-and-agents',
        title: 'One “best” AI for every task is a weak strategy',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Uber selects models for specific workloads by comparing cost per completed task, quality, and reliability. A primary model can plan and evaluate work while more economical models perform well-defined subtasks. This lowers cost without automatically lowering quality.',
          },
          {
            type: 'paragraph',
            text: 'The same specialization applies at the business level. An agent preparing a weekly status does not need the same instructions, tools, and autonomy as one analyzing an incident or reviewing a contract. A digital team should consist of roles designed around processes, rather than copies of one universal chatbot.',
          },
        ],
      },
      {
        id: 'what-uber-results-mean',
        title: 'What Uber’s results mean for other companies',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'From February to August 2026, weekly active users across Uber’s agentic offerings grew sevenfold and weekly agentic requests grew 9.4 times. Holding one model fixed, cost per 1,000 requests fell about 34% from its peak, while cost per session fell 52% from its June peak. Uber notes that individual results depend on the codebase, team size, and agent workflows.',
          },
          {
            type: 'paragraph',
            text: 'Copying the percentages would be meaningless. The useful part to copy is the method: select real work, build a benchmark from its examples, measure outcome quality and cost, and then improve model routing, context, tools, and instructions.',
          },
          {
            type: 'list',
            items: [
              'Begin with one recurring process with a clear owner.',
              'Record a baseline for time, quality, errors, and manual context handoffs.',
              'Identify the required sources and repair critical data gaps.',
              'Bound the agent’s role and actions, with human review or approval.',
              'Measure the quality and cost of a completed outcome.',
              'Expand the digital team only after validating the pilot.',
            ],
          },
        ],
      },
      {
        id: 'laplace-digital-team',
        title: 'How Laplace extends this approach',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace brings the principle of managed agents from specialized technical use cases into everyday company workflows. An organization can create digital employees with different roles and skills, connect approved work tools, and run recurring tasks on a schedule.',
          },
          {
            type: 'paragraph',
            text: 'The path begins with process analysis, not mass agent creation. A company needs to understand where context is lost, which actions repeat, what outcome can be verified, and where a person must remain involved. A limited scenario then becomes a measurable pilot, and validated roles can grow into a digital team.',
          },
          {
            type: 'callout',
            title: 'A Software Factory is one instance of a broader idea',
            body: 'Uber demonstrates the model across the software development lifecycle. Laplace extends it to other processes that depend on finding, comparing, and transferring company context.',
          },
        ],
      },
      {
        id: 'faq-uber-ai-agents',
        title: 'Frequently asked questions about AI agents and Uber’s approach',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'What does Uber mean by a Software Factory?',
                answer:
                  'It is a system of AI tools and managed agents across software development, including code generation and review, CI repair, incident work, and maintenance. Uber manages models, context, tools, cost, and outcome quality through a shared platform.',
              },
              {
                question: 'How is a managed AI agent different from a chatbot?',
                answer:
                  'A chatbot usually responds to an individual user request. A managed agent has a defined role, tools, context sources, boundaries, and an outcome criterion; it can also perform a recurring process on a schedule with human oversight.',
              },
              {
                question: 'Which AI agents are a good fit for business?',
                answer:
                  'A strong first use case repeats regularly, uses several work sources, has a verifiable outcome, and allows human control. Examples include project summaries, task quality checks, customer response preparation, and documentation discrepancy detection.',
              },
              {
                question: 'Does Laplace have a Knowledge Graph?',
                answer:
                  'Not yet. Laplace digital employees work with connected systems and documents within the workspace available to them. A unified Knowledge Graph is on the backlog as a future direction for the shared context foundation of a digital team.',
              },
              {
                question: 'How should a digital employee be measured?',
                answer:
                  'The metric should reflect the outcome of a specific process: time spent, completeness, accuracy, accepted recommendations, errors, or cost per completed task. Message count and agent count do not show business value on their own.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'en',
    type: 'research',
    slug: 'ai-native-sdlc-playbook',
    title:
      'Code Learned to Write Itself. Now Software Development Must Learn to Record Decisions',
    description:
      'Code now appears in hours. We ask why releases still take weeks, what Anthropic wants to change across the SDLC, and where Laplace fits in the new loop.',
    author: 'Laplace Team',
    publishedAt: '2026-09-07',
    readingTime: '12 min read',
    coverImage: '/images/ai-native-sdlc-playbook.png',
    coverImageAlt:
      'Six AI-native SDLC stages form a continuous context loop with human review checkpoints',
    topics: [
      'AI-native SDLC',
      'Agentic SDLC',
      'AI software development',
      'Software development automation',
      'Digital employees',
      'Enterprise context',
    ],
    integrations: ['Jira', 'Confluence', 'GitHub', 'Documents'],
    published: false,
    seoTitle: 'AI-Native SDLC: Anthropic’s Playbook and Laplace',
    seoDescription:
      'What AI-native SDLC means, why code generation is not enough, and how Laplace connects tasks, requirements, code, and human control.',
    relatedResources: [
      'uber-ai-agents-software-factory',
      'programmers-manage-ai-coding-agents',
      'digital-employees',
      'what-is-context-debt',
      'choose-ai-workflow',
    ],
    ctaType: 'context-audit',
    keyTakeaways: [
      'Code has become cheap. Understanding remains expensive: what to build, why, under which constraints, and what changed after generation.',
      'Anthropic replaces the linear pipeline with a continuous loop in which each stage leaves reviewable memory for the next.',
      'Laplace belongs between people, agents, and systems—where working context usually breaks apart.',
    ],
    sections: [
      {
        id: 'code-is-not-the-bottleneck',
        title: 'The release that stalled after the code was ready',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Imagine that a team gives a task to a coding agent in the morning and receives a working diff with tests by lunch. The release should happen tomorrow. Then the team discovers incomplete acceptance criteria, an old specification that disagrees with the solution, a security review scheduled for Thursday, and a restriction nobody can explain anymore.',
          },
          {
            type: 'paragraph',
            text: 'The code is ready. The work is not. Anthropic’s AI-Native SDLC Playbook begins with this paradox. When Build contracts from weeks to hours, the old process does not accelerate as a whole. It simply reaches Plan, Test, Deploy, and Maintain sooner—stages that still move at human speed.',
          },
          {
            type: 'source',
            label: 'Primary source',
            description:
              'Louis Claxton, Anthropic, “The AI-Native SDLC playbook,” August 21, 2026.',
            href: 'https://claude.com/blog/the-ai-native-sdlc-playbook',
          },
          {
            type: 'callout',
            title: 'AI did not remove the queue. It moved it',
            body: 'Faster code generation delivers work sooner to requirements, verification, and approval systems that were never designed for agent speed.',
          },
        ],
      },
      {
        id: 'what-is-ai-native-sdlc',
        title: 'Anthropic bends the pipeline into a loop',
        level: 2,
        blocks: [
          {
            type: 'metrics',
            items: [
              {
                value: '6',
                label: 'SDLC stages',
                detail: 'Plan, Design, Build, Test, Deploy, Maintain',
              },
              {
                value: '1',
                label: 'continuous loop',
                detail: 'each accepted outcome triggers the next step',
              },
              {
                value: 'hours',
                label: 'instead of weeks',
                detail: 'the target cycle speed',
              },
              {
                value: 'human',
                label: 'above the loop',
                detail: 'directing, reviewing, and governing risk',
              },
            ],
            sourceNote:
              'Anthropic’s AI-native SDLC model. These are target principles, not guaranteed results for every team.',
          },
          {
            type: 'paragraph',
            text: 'A traditional SDLC resembles a relay race. An idea moves to a product manager, requirements to an architect, design to an engineer, and code to QA and release teams. Each role receives a document, ticket, or link and tries to reconstruct what the previous person meant.',
          },
          {
            type: 'paragraph',
            text: 'Anthropic asks teams to stop treating Plan, Design, Build, Test, Deploy, and Maintain as six isolated rooms. In an AI-native SDLC they form one loop: an accepted outcome starts the next stage, while a production incident returns to the beginning as new intent.',
          },
          {
            type: 'paragraph',
            text: 'People do not disappear from the process. They move above the loop, setting direction, approving intent, accepting risk, and handling exceptions. Agents take the work that can be repeated, verified, and handed forward without another meeting.',
          },
          {
            type: 'list',
            items: [
              'Plan: the original problem becomes intent that both a person and an agent can understand.',
              'Design: accepted intent becomes requirements and a technical specification constrained by company standards.',
              'Build: an agent creates the plan, code, tests, and supporting documentation.',
              'Test: checks and evals run continuously instead of only at a stage boundary.',
              'Deploy: agentic review handles standard checks while people focus on regulated and critical decisions.',
              'Maintain: agents monitor operational signals, diagnose problems, and return them to a new cycle.',
            ],
          },
        ],
      },
      {
        id: 'artifacts-connect-the-loop',
        title: 'Every decision needs to leave a memory',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In Anthropic’s model, each stage ends with a saved artifact that the next agent can read and a person can review: intent, specification, plan, diff with tests, pull request with review findings, or incident record. The revision history becomes an audit trail showing who requested the change, what the agent produced, and who approved it.',
          },
          {
            type: 'paragraph',
            text: 'The principle matters more than the file format. A company can keep intent in Git, Jira, or another system if it defines the source of truth and links related objects. Trouble begins when the ticket is separated from the decision, the specification from the implementation, and the reason for a change from its recorded history.',
          },
          {
            type: 'callout',
            title: 'AI-native SDLC is continuity of context',
            body: 'The agent at the next stage should not have to guess why a change exists, which constraints apply, and what has already been approved. It should continue a reviewable chain of work.',
          },
        ],
      },
      {
        id: 'context-generation-context',
        title: 'We call it “Context → Generation → Context”',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Two months before Anthropic published its playbook, Laplace founder Alexander Nekrashenko described the same problem from an engineering team’s perspective. When an agent generates a thousand lines in an hour, system understanding no longer emerges automatically through the work of writing code. A team can receive a working result while losing the knowledge of why the system was built that way.',
          },
          {
            type: 'paragraph',
            text: 'The proposed new loop is “Context → Generation → Context.” Before generation, the team gathers the task, constraints, architecture, and rules for the agent. After generation, it returns decision rationale, task links, change descriptions, and new product knowledge to the work systems. The next iteration starts from that recorded context instead of investigating the code again.',
          },
          {
            type: 'source',
            label: 'A column by the Laplace founder',
            description:
              'Alexander Nekrashenko, “A thousand lines of code in an hour. Losing system understanding in three days,” published in Russian on vc.ru.',
            href: 'https://vc.ru/ai/2981658-ponimanie-sistemy-v-razrabotke-kod-protiv-konteksta',
          },
          {
            type: 'paragraph',
            text: 'Anthropic formalizes this principle across the complete SDLC. Intent, specification, plan, diff, review findings, and incident record become consecutive carriers of context. The formats differ, but the objective is the same: prevent understanding from disappearing between generation, review, and the next iteration.',
          },
          {
            type: 'callout',
            title:
              'Code remains an output but stops being the only carrier of knowledge',
            body: 'AI-native development needs a separate, managed context system that records what changed, why, which requirements support it, and who approved it.',
          },
        ],
      },
      {
        id: 'laplace-context-layer',
        title: 'Laplace works one level above the code',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A coding agent sees the repository. The reason it needs to change the code usually lives somewhere else. A customer complained in one system, a product manager created a task in another, requirements changed in Confluence, an architectural exception was discussed in a meeting, and the previous implementation can only be reconstructed from GitHub.',
          },
          {
            type: 'paragraph',
            text: 'Laplace is building a team layer across those gaps rather than another code generator. Inside a workspace, digital employees receive approved sources, a defined role, and tools. One reconstructs feature context, another checks the brief, and a third prepares a release package. Recurring work can run on a schedule, while consequential actions stay with a person for approval.',
          },
          {
            type: 'list',
            items: [
              'Jira provides tasks, statuses, comments, participants, and work history.',
              'Confluence contains requirements, decisions, procedures, and project documentation.',
              'GitHub links requirements with issues, pull requests, diffs, and implementation files.',
              'Workspace documents add specifications, spreadsheets, presentations, and other supporting material.',
              'Context Audit reveals incomplete tasks, documentation gaps, weak links, and unresolved decisions.',
              'Digital employees collect, verify, and transfer context for a specific process role.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Laplace does not replace an IDE, CI/CD platform, or coding agent. Its role is to make company context available to a managed digital team and reduce information loss between people, agents, and systems.',
          },
        ],
      },
      {
        id: 'plan-and-design',
        title: 'Monday. The request has not become a task yet',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'The new loop does not begin with “build this feature.” On Monday, the team first captures intent: whose problem this is, which outcome matters, which systems are affected, where the boundaries sit, and what remains unknown. In Anthropic’s model, a product owner reviews that draft before it becomes the basis for design.',
          },
          {
            type: 'paragraph',
            text: 'In Laplace, a digital analyst or coordinator can collect related context from tasks, documentation, past decisions, and customer material, identify contradictions, and draft a structured problem statement. Context Audit can show what information is missing before work reaches engineering.',
          },
          {
            type: 'list',
            items: [
              'Find the original request and affected users.',
              'Compare the task with current requirements and past decisions.',
              'Identify missing acceptance criteria, constraints, and owners.',
              'Prepare a structured brief for product and engineering.',
              'Leave requirements approval with the accountable person.',
            ],
          },
        ],
      },
      {
        id: 'build-and-test',
        title:
          'Wednesday. The code is ready, but understanding still needs review',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'By Wednesday, the specification becomes a plan, code, and tests. The coding agent is finally in its natural environment. Anthropic recommends keeping project rules in version-controlled instructions, running checks before claiming completion, and beginning a bug fix with a test that reproduces the failure.',
          },
          {
            type: 'paragraph',
            text: 'Laplace can prepare the input context for that session: related requirements, decisions, tasks, pull requests, and relevant documents. After implementation, a digital employee can compare the diff with the original request, verify link coverage, and surface questions for review. Executing tests and editing code remain responsibilities of the coding agent and engineering toolchain.',
          },
          {
            type: 'callout',
            title: 'A strong coding agent cannot repair a weak brief',
            body: 'If the input intent is incomplete or contradicts the documentation, an agent can implement the wrong change very quickly. Plan and Design quality therefore becomes part of code quality.',
          },
        ],
      },
      {
        id: 'deploy-and-maintain',
        title: 'Friday. A release ends with new memory, not the Deploy button',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'By Friday, the question is no longer whether the agent can press Deploy. The question is what may pass automatically and where the company must stop and make a decision. Anthropic gives standard checks to agents and preserves human attention for critical, regulated, and ambiguous changes.',
          },
          {
            type: 'paragraph',
            text: 'In the current Laplace product, actions depend on the tools and workspace permissions available to an agent. Proposed changes can remain drafts until a person explicitly approves them. A digital release coordinator can assemble tasks, changes, documentation, and open questions into one release package, while accountable people make the release decision.',
          },
          {
            type: 'paragraph',
            text: 'After release, a digital employee can regularly collect signals from connected sources, relate incidents to changes, and prepare context for the next task. A fully event-driven loop in which a production alert automatically creates intent and launches a chain of agents is not a current Laplace capability.',
          },
        ],
      },
      {
        id: 'current-boundaries',
        title: 'Where the elegant diagram meets reality',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In a diagram, the loop closes with one arrow. Inside a company, those arrows cross legacy systems, different owners, security requirements, and accumulated Context Debt. AI-native SDLC is therefore an operating model rather than a feature that can be enabled with one switch.',
          },
          {
            type: 'list',
            items: [
              'Laplace already connects work sources and supports specialized digital employees.',
              'Context Audit already helps identify Context Debt in available data.',
              'Laplace automations run one configured agent on a schedule through its available tools.',
              'Arbitrary event-driven orchestration across all six SDLC stages is not currently implemented.',
              'A unified Knowledge Graph and Process Discovery Agent are in development and are not available product features.',
            ],
          },
          {
            type: 'paragraph',
            text: 'A practical adoption path is therefore to use Laplace alongside coding agents: first improve context and specific roles at the most painful handoffs, measure the effect, and then connect stages that have already proved reliable.',
          },
        ],
      },
      {
        id: 'how-to-start',
        title: 'What a team can change on Monday',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A team does not need to announce a transformation of all six stages on Monday. It needs to find one place where work repeatedly stops to reconstruct context and ask eight practical questions about it.',
          },
          {
            type: 'list',
            items: [
              '1. Choose one handoff: idea to brief, specification to development, pull request to release, or incident to new task.',
              '2. Record a baseline for wait time, rework, missed requirements, and manual information search.',
              '3. Define the source of truth and links between Jira, Confluence, GitHub, and supporting documents.',
              '4. Run a Context Audit and repair critical gaps.',
              '5. Create one digital role with bounded tools and a verifiable outcome.',
              '6. Define the human approval point and accountability for the decision.',
              '7. Compare the duration, completeness, and quality of the new process with the baseline.',
              '8. Automate the next handoff only after the first produces stable results.',
            ],
          },
          {
            type: 'callout',
            title: 'Start where context is lost',
            body: 'The most useful first automation often sits outside code generation, where the team repeatedly reconstructs what must be done, why, and under which constraints.',
          },
        ],
      },
      {
        id: 'faq-ai-native-sdlc',
        title: 'Frequently asked questions about AI-native SDLC',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'What is an AI-native SDLC?',
                answer:
                  'It is a software development lifecycle in which AI participates at every stage, from capturing intent and designing a change through testing, release, and maintenance. Reviewable artifacts connect the stages, while people approve decisions that require judgment and accountability.',
              },
              {
                question: 'How is AI-native SDLC different from AI coding?',
                answer:
                  'AI coding accelerates the creation and modification of code. AI-native SDLC redesigns the complete process around that acceleration, including planning, requirements transfer, verification, governance, release, and the return of production knowledge into the next cycle.',
              },
              {
                question: 'Does Laplace replace Claude Code?',
                answer:
                  'No. A coding agent works directly with code and engineering tools. Laplace helps collect company context from work systems, identify gaps, and organize digital roles around the process.',
              },
              {
                question: 'Can Laplace automate the complete SDLC?',
                answer:
                  'Not yet. Laplace supports specialized agents, connected work sources, Context Audit, and scheduled runs. Fully event-driven orchestration across all six SDLC stages is not a current product capability.',
              },
              {
                question: 'Which SDLC stage should a company start with?',
                answer:
                  'Start with a transition where the team regularly loses time or context and can measure the result: preparing a brief, collecting context before development, checking implementation against requirements, or assembling a release package.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'en',
    type: 'research',
    slug: 'programmers-manage-ai-coding-agents',
    title:
      'Developers Are No Longer Writing Code by Hand. They Manage the Agents That Write It',
    description:
      'Claude authors more than 80% of merged code at Anthropic, while OpenAI built a million-line product without manual coding. Here is what engineers do next.',
    author: 'Laplace Team',
    publishedAt: '2026-09-07',
    readingTime: '11 min read',
    coverImage: '/images/programmers-manage-ai-coding-agents.png',
    coverImageAlt:
      'An engineer directs several AI agents that plan, write, and verify code in parallel',
    topics: [
      'AI coding agents',
      'Claude Code',
      'OpenAI Codex',
      'Kimi Code',
      'Agentic Coding',
      'Future of Software Engineering',
    ],
    integrations: ['GitHub', 'Jira', 'Confluence'],
    published: false,
    seoTitle: 'AI Agents Write Code: Claude Code, Codex, and Kimi',
    seoDescription:
      'Claude authors over 80% of Anthropic code, while Codex built a million-line product. How coding agents change engineering and where Laplace fits.',
    relatedResources: [
      'ai-native-sdlc-playbook',
      'uber-ai-agents-software-factory',
      'what-is-context-debt',
      'digital-employees',
    ],
    ctaType: 'context-audit',
    keyTakeaways: [
      'In leading AI companies, typing code is no longer the engineer’s main job: people specify intent, shape the environment, review outcomes, and make decisions.',
      'Claude Code, Codex, and Kimi Code work across repositories, commands, and tests, making them execution agents rather than line completion tools.',
      'As code becomes faster to produce, context, requirements, architectural decisions, and governance become the constraint that Laplace helps organize.',
    ],
    sections: [
      {
        id: 'five-months-without-code',
        title: 'Five months without writing a line of code',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'In February 2026, OpenAI described an internal experiment that would recently have sounded like provocation. A small team built a working product of roughly one million lines, including application code, tests, CI, documentation, observability, and internal tools. Humans manually wrote none of it; Codex created the entire repository.',
          },
          {
            type: 'paragraph',
            text: 'Three engineers directed the agent through tasks and feedback. Over five months, the team merged about 1,500 pull requests and estimated that development took roughly one tenth of the usual time. This was not only a benchmark: hundreds of internal users and external alpha testers used the product.',
          },
          {
            type: 'source',
            label: 'OpenAI: Harness engineering in an agent-first world',
            href: 'https://openai.com/index/harness-engineering/',
            description:
              'OpenAI’s account of a product whose complete codebase was written by Codex.',
          },
          {
            type: 'callout',
            title: 'People set direction. Agents execute',
            body: 'The engineer remains in the process. What disappears is the need to type every implementation personally; the work shifts to intent, environment design, feedback, and judgment.',
          },
        ],
      },
      {
        id: 'anthropic-crossed-eighty-percent',
        title: 'Anthropic has already crossed 80%',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'At Anthropic, this is no isolated experiment. The company says that by May 2026, Claude authored more than 80% of code merged into its codebase. Before the Claude Code research preview launched in February 2025, that share was in the low single digits.',
          },
          {
            type: 'metrics',
            items: [
              {
                value: '>80%',
                label: 'of Anthropic code authored by Claude',
                detail: 'Share of merged code as of May 2026.',
              },
              {
                value: '8×',
                label: 'more lines per engineer',
                detail: 'Q2 2026 compared with 2024.',
              },
              {
                value: '+67%',
                label: 'pull requests per engineer',
                detail:
                  'Increase Anthropic associates with Claude Code adoption.',
              },
              {
                value: '70–90%',
                label: 'code involving Claude Code',
                detail:
                  'Range across Anthropic engineering teams in January 2026.',
              },
            ],
            sourceNote:
              'Anthropic internal data. Lines of code and pull requests measure output volume rather than quality, a limitation Anthropic explicitly acknowledges.',
          },
          {
            type: 'source',
            label: 'Anthropic: When AI builds itself',
            href: 'https://www.anthropic.com/institute/recursive-self-improvement',
            description:
              'Anthropic data on Claude-authored code and engineering throughput.',
          },
          {
            type: 'source',
            label: 'Anthropic: Claude Code contribution metrics',
            href: 'https://claude.com/blog/contribution-metrics',
            description:
              'How Anthropic measures assisted code and pull requests associated with Claude Code.',
          },
        ],
      },
      {
        id: 'from-autocomplete-to-agent',
        title: 'This is no longer autocomplete',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'First-generation copilots waited for a person to open a file and start typing. A coding agent receives a goal, explores the repository, edits several files, runs commands and tests, reads failures, and repeats the loop. Claude Code and Codex made this mode mainstream; Kimi Code is built around the same operating model.',
          },
          {
            type: 'source',
            label: 'Kimi: Kimi Code introduction',
            href: 'https://www.kimi.ai/resources/kimi-code-introduction',
            description:
              'The official description of an agent that explores repositories, plans, runs commands, and iterates autonomously.',
          },
          {
            type: 'list',
            items: [
              'Autocomplete suggests a fragment; an agent completes an outcome.',
              'Autocomplete sees the cursor; an agent explores relationships across a repository.',
              'Autocomplete waits for a person; an agent calls tools and responds to their results.',
              'People used to edit the implementation; increasingly, they edit intent, constraints, and acceptance criteria.',
            ],
          },
        ],
      },
      {
        id: 'what-programmer-does-now',
        title: 'If engineers do not type code, what do they do?',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'The closest analogy is a shift from implementer to director of a production system. A strong engineer chooses the problem, turns it into a verifiable task, gives the agent the right tools, defines architectural boundaries, and builds a short feedback loop.',
          },
          {
            type: 'list',
            items: [
              'State intent and acceptance criteria before launching an agent.',
              'Make the repository legible through commands, rules, architecture, and constraints.',
              'Split a large goal into parts that can be verified independently.',
              'Review system behavior, risk, and fit with the business need.',
              'Turn recurring feedback into tests, rules, and automated checks.',
              'Decide what is worth building, not only how to implement it.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Knowledge of languages and architecture remains critical because it lets a person distinguish a sound solution from a plausible mistake. Typing syntax, however, becomes a smaller share of the job.',
          },
        ],
      },
      {
        id: 'the-new-bottleneck',
        title: 'Code became cheap. Decisions did not',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'When an agent can produce ten implementations in the time one used to take, code stops being scarce. Human attention and context become scarce instead: the current brief, decision history, security constraints, customer data, and an understanding of how the change affects other processes.',
          },
          {
            type: 'callout',
            title: 'Agent speed multiplies context quality',
            body: 'A well-defined decision quickly becomes a working system. An outdated brief just as quickly becomes dozens of files, tests, and a confidently presented wrong pull request.',
          },
          {
            type: 'paragraph',
            text: 'The next competitive gap will therefore appear between companies whose agents can see coherent operational context and companies that feed them a random bundle of tickets, documents, and messages.',
          },
        ],
      },
      {
        id: 'where-laplace-fits',
        title: 'Why coding agents need Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace does not replace Claude Code, Codex, or Kimi Code and does not compete for space in the editor. A coding agent executes inside the repository. Laplace works with team and process context by connecting work sources, running a Context Audit, finding gaps, and organizing specialized digital roles with bounded tools.',
          },
          {
            type: 'paragraph',
            text: 'A practical combination looks like this: Jira holds the task, Confluence holds requirements and decisions, and GitHub holds the implementation. Laplace helps an agent or a person assemble and check the connected context; the coding agent implements the change and returns its result to the process.',
          },
          {
            type: 'callout',
            title: 'Current product boundary',
            body: 'Laplace currently supports connected sources, Context Audit, specialized agents, and scheduled runs. A unified Knowledge Graph and fully event-driven multi-agent orchestration are in development and are not yet available.',
          },
        ],
      },
      {
        id: 'what-to-measure',
        title: 'Do not count lines. Count outcomes',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Eight times more lines can signal acceleration, or eight times more code to maintain. Companies should evaluate coding agents using outcomes that matter to users and the business.',
          },
          {
            type: 'list',
            items: [
              'Time from an approved decision to a working change.',
              'Share of tasks that pass review without reimplementation.',
              'Defects and rollbacks after release.',
              'Human attention required per completed task.',
              'Completeness of requirements and decisions retained for the next cycle.',
            ],
          },
        ],
      },
      {
        id: 'faq-coding-agents',
        title: 'Frequently asked questions about coding agents',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question:
                  'Is it true that programmers no longer write code by hand?',
                answer:
                  'In some leading teams, yes. Anthropic says Claude authored more than 80% of merged code, while an experimental OpenAI team built a product without manual coding. There is no equivalent statistic for the entire industry yet.',
              },
              {
                question:
                  'How is a coding agent different from AI autocomplete?',
                answer:
                  'Autocomplete suggests fragments near the cursor. A coding agent receives a task, explores the repository, edits files, runs commands and tests, fixes errors, and can prepare a pull request.',
              },
              {
                question:
                  'Will Claude Code, Codex, and Kimi replace programmers?',
                answer:
                  'They reduce manual typing and change the composition of the job. People remain responsible for requirements, architecture, security, validation, and product decisions, even as one engineer can direct more work.',
              },
              {
                question: 'How should a company prepare for coding agents?',
                answer:
                  'Start with one repository and a measurable task class. Capture rules and commands, connect tests, define human approval points, and verify the quality of context across Jira, documentation, and code.',
              },
              {
                question: 'How does Laplace relate to AI coding?',
                answer:
                  'Laplace operates at the team context and process layer. It helps collect information from work systems, identify gaps, and organize digital roles around a coding agent without replacing the coding tool.',
              },
            ],
          },
        ],
      },
    ],
  },
]
